import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  memo,
} from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Scrollbar from 'react-scrollbars-custom';
import {Tooltip as ReactTooltip} from 'react-tooltip';

import ResourceAreaHeader from '../ResourceAreaHeader';
import ResourcesBlock from './ResourcesBlock';
import RowContent from './RowContent';
import Cell from './Cell';
import classes from './MonthView.module.scss';
import classnames from 'classnames';

const trackYProps = {
  renderer: ({ elementRef, ...props }) => (
    <span
      {...props}
      ref={elementRef}
      className={classes.scrollableContent__scrollbarTrackY}
    />
  ),
};

const MonthView = ({
  resources,
  sheet,
  fields,
  holidays,
  employeesWithOvertime,
  requestBehalves,
  onChangeMonth,
  withCost,
  withBonus,
  withAccumulated,
  accumulatedMonths,
  mergeTimesheetPlaces,
  aggregatedAiResults,
}) => {
  const { t, i18n } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'));
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const topScrollRef = useRef(null);
  const topScrollThumbRef = useRef(null);

  useEffect(() => {
    const contentEl = contentRef.current;
    const headerEl = headerRef.current;
    const topScrollEl = topScrollRef.current;
    const topScrollThumbEl = topScrollThumbRef.current;
    if (!contentEl || !headerEl || !topScrollEl || !topScrollThumbEl) return;

    let rafId = 0;
    let isDragging = false;
    let dragOffsetX = 0;

    const getSizes = () => {
      const visibleWidth = contentEl.clientWidth;
      const totalWidth = contentEl.scrollWidth;
      const trackWidth = topScrollEl.clientWidth;
      const thumbWidth = totalWidth > 0 ? Math.max((visibleWidth / totalWidth) * trackWidth, 24) : trackWidth;
      const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
      const maxScrollLeft = Math.max(totalWidth - visibleWidth, 0);

      return {
        thumbWidth,
        maxThumbLeft,
        maxScrollLeft,
      };
    };

    const updateThumb = () => {
      const { thumbWidth, maxThumbLeft, maxScrollLeft } = getSizes();
      const thumbLeft = maxScrollLeft > 0
        ? (contentEl.scrollLeft / maxScrollLeft) * maxThumbLeft
        : 0;

      topScrollThumbEl.style.width = `${thumbWidth}px`;
      topScrollThumbEl.style.transform = `translateX(${thumbLeft}px)`;
    };

    const syncScrollLeft = (scrollLeft) => {
      if (headerEl.scrollLeft !== scrollLeft) {
        headerEl.scrollLeft = scrollLeft;
      }
      if (contentEl.scrollLeft !== scrollLeft) {
        contentEl.scrollLeft = scrollLeft;
      }
      updateThumb();
    };

    const handleContentScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        syncScrollLeft(contentEl.scrollLeft);
      });
    };

    const setScrollFromClientX = (clientX) => {
      const rect = topScrollEl.getBoundingClientRect();
      const { thumbWidth, maxThumbLeft, maxScrollLeft } = getSizes();
      if (maxThumbLeft <= 0 || maxScrollLeft <= 0) return;

      const rawLeft = clientX - rect.left - dragOffsetX;
      const nextThumbLeft = Math.max(0, Math.min(rawLeft, maxThumbLeft));
      const nextScrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
      syncScrollLeft(nextScrollLeft);
    };

    const handleTrackMouseDown = (e) => {
      if (e.target === topScrollThumbEl) return;
      const thumbRect = topScrollThumbEl.getBoundingClientRect();
      dragOffsetX = thumbRect.width / 2;
      setScrollFromClientX(e.clientX);
    };

    const handleThumbMouseDown = (e) => {
      e.preventDefault();
      const thumbRect = topScrollThumbEl.getBoundingClientRect();
      dragOffsetX = e.clientX - thumbRect.left;
      isDragging = true;
      document.body.style.userSelect = 'none';
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setScrollFromClientX(e.clientX);
    };

    const handleMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      document.body.style.userSelect = '';
    };

    const resizeObserver = new ResizeObserver(() => {
      updateThumb();
      syncScrollLeft(contentEl.scrollLeft);
    });

    updateThumb();
    syncScrollLeft(contentEl.scrollLeft);

    resizeObserver.observe(contentEl);
    if (contentEl.firstElementChild) {
      resizeObserver.observe(contentEl.firstElementChild);
    }
    resizeObserver.observe(topScrollEl);

    contentEl.addEventListener('scroll', handleContentScroll, { passive: true });
    topScrollEl.addEventListener('mousedown', handleTrackMouseDown);
    topScrollThumbEl.addEventListener('mousedown', handleThumbMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      contentEl.removeEventListener('scroll', handleContentScroll);
      topScrollEl.removeEventListener('mousedown', handleTrackMouseDown);
      topScrollThumbEl.removeEventListener('mousedown', handleThumbMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [resources.length, fields.length, currentMonth, withCost, withAccumulated, accumulatedMonths]);
  
  const handleClickPrevMonth = () => {
    const nextMonth = currentMonth.clone().add(-1, 'months');
    setCurrentMonth(nextMonth);
    onChangeMonth({ fromDate: nextMonth });
  };
  const handleClickNextMonth = () => {
    const nextMonth = currentMonth.clone().add(1, 'months');
    setCurrentMonth(nextMonth);
    onChangeMonth({ fromDate: nextMonth });
  };

  const daysOfMonth = useMemo(() => {
    const day = currentMonth.clone().add(-1, 'days');
    const arr = new Array(currentMonth.daysInMonth()).fill().map((_, index) => {
      const dayNumber = day.add(1, 'days').day();
      const currentDay = moment();

      return {
        id: index + 1,
        title: index + 1,
        weekend: dayNumber === 6 || dayNumber === 0,
        today: currentDay.isSame(day, 'day'),
        holiday: holidays && holidays[index + 1] ? holidays[index + 1] : false,
        date: day.clone().format('YYYY-MM-DD'),
      };
    });
    arr.push({
      id: 'totalTime',
      title: t('Total Time'),
      statistic: true,
    });

    if (withAccumulated) {
      if (accumulatedMonths > 1) {
        arr.push({
          id: 'plannedTimeMonth',
          title: t('Planned Time _s') + ' ('+t('month')+')',
          statistic: true,
        });
  
        arr.push({
          id: 'targetTimeMonth',
          title: t('Target Time _s') + ' ('+t('month')+')',
          statistic: true,
        }); 
      }

      arr.push({
        id: 'plannedTime',
        title: t('Planned Time _s') + ' ('+t('period')+')',
        statistic: true,
      });

      arr.push({
        id: 'targetTime',
        title: t('Target Time _s') + ' ('+t('period')+')',
        statistic: true,
      });      
    }

    if (withCost) {
      arr.push({
        id: 'totalCost',
        title: t('Total Cost'),
        statistic: true,
      });
    }

    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, currentMonth, withCost, withAccumulated, accumulatedMonths, holidays]);
  // #828688
  //fix for employee block height
  const empHeight = {
    0: 130,
    1: 130,
    2: 130,
    3: 139,
    4: 180,
    5: 221,
    6: 262,
    7: 303,
    8: 344,
    9: 385,
    10: 426,
  }

  return (
    <>
      <div className={classes.sheetmonthView}>
        <div className={classes.sheetmonthView__header}>
          <ResourceAreaHeader
            title={currentMonth.format('MMMM yyyy')}
            onClickPrev={handleClickPrevMonth}
            onClickNext={handleClickNextMonth}
          />
          <div className={classnames(classes.sheetmonthView__header__dataOuter, {
            [classes.sheetmonthView__header__dataOuter_withCost]: withCost,
            [classes.sheetmonthView__header__dataOuter_withAccumulated]: withAccumulated,
            [classes.sheetmonthView__header__dataOuter_withMonth]: withAccumulated && accumulatedMonths > 1
            })}>
            <div
              ref={headerRef}
              className={classes.sheetmonthView__header__data}>
              <Cell
                key={0}
                title={t('Article name')}
                article={true}
                header
              />
              {
                daysOfMonth.map((item, index, array) => {
                  const firstStaticticIndex = array.findIndex(el => el.statistic)
  
                  return (
                    <Cell
                      key={item.id}
                      cellId={item.id}
                      title={item.title}
                      statistic={item.statistic}
                      today={item.today}
                      holiday={item.holiday}
                      header
                      withBorder={index === firstStaticticIndex}
                      tooltip={item.id === 'plannedTime' ? t('Planned Time _t') : item.id === 'targetTime' ? t('Target Time _t') : ''}
                    />
                  )
                })
              }
            </div>
          </div>
          <div className={classes.sheetmonthView__header__empty} />
        </div>
        <Scrollbar
          className={classes.scrollableContent}
          trackYProps={trackYProps}
          noScrollX
        >
          <div className={classes.sheetmonthView__content}>
            <ResourcesBlock
              resources={resources}
              employeesWithOvertime={employeesWithOvertime}
              currentMonth={currentMonth}
              height={empHeight[fields.length]}
              mergeTimesheetPlaces={mergeTimesheetPlaces}
              aggregatedAiResults={aggregatedAiResults}
            />
            <div className={classnames(classes.sheetmonthView__content__dataOuter, {
              [classes.sheetmonthView__content__dataOuter_withCost]: withCost,
              [classes.sheetmonthView__content__dataOuter_withAccumulated]: withAccumulated,
              [classes.sheetmonthView__content__dataOuter_withMonth]: withAccumulated && accumulatedMonths > 1
            })}>
              <div ref={topScrollRef} className={classes.sheetmonthView__content__topScroll}>
                <div ref={topScrollThumbRef} className={classes.sheetmonthView__content__topScroll__thumb} />
              </div>
              <div 
                ref={contentRef}
                className={classes.sheetmonthView__content__data}>
                {
                  resources.map((resource, index) => (
                    <div
                      key={resource.id+'_'+resource.place_id+'_'+index}
                      id={resource.id+'_'+resource.place_id+'_'+index}
                      style={{marginTop: !index ? 0 : 8}}
                      className={classes.sheetmonthView__content__data__wrap}>
                      {
                        resource ?
                          fields.map((item) => {
                            return (
                              <RowContent
                                key={resource.id+'-'+item}
                                field={item}
                                resource={resource}
                                sheet={sheet}
                                mergeTimesheetPlaces={mergeTimesheetPlaces}
                                employeesWithOvertime={employeesWithOvertime}
                                daysOfMonth={daysOfMonth}
                                requestBehalves={requestBehalves}
                              />
                            )
                          }) : null
                      }
                    </div>
                  ))
                }
              </div>
            </div>
            <div className={classes.sheetmonthView__content__srollPanel} />
          </div>
        </Scrollbar>
        {/* <div className={classes.sheetmonthView__content__fakeScroll}></div> */}
      </div>
      <ReactTooltip
        id='title'
        className={classes.sheetmonthView__content__data__tooltip}
        effect='solid'
        html={true}
      />
      <ReactTooltip
        id='holiday'
        className={classes.sheetmonthView__content__data__tooltip}
        effect='solid'
      />
    </>
  );
};

const isEqual = (prev, next) => {
  return prev.sheet === next.sheet && prev.aggregatedAiResults === next.aggregatedAiResults && prev.resources === next.resources
}

export default memo(MonthView, isEqual)
